import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { Calendar, Fuel, TrendingUp, Clock, User, Euro, AlertTriangle, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function for translations (fr, ru, md, uk)
const getText = (language, fr, ru, md, uk) => {
  if (language === 'ru') return ru;
  if (language === 'md') return md;
  if (language === 'uk') return uk;
  return fr;
};

export default function StatsPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isManager = user?.role === 'manager';
  const [attendanceStats, setAttendanceStats] = useState([]);
  const [fuelStats, setFuelStats] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('me');
  const [productivity, setProductivity] = useState(null);
  // Charges fixes mensuelles (editeur inline, manager)
  const [fcEditing, setFcEditing] = useState(false);
  const [fcItems, setFcItems] = useState([]);
  const [fcSaving, setFcSaving] = useState(false);
  // Objectif de net minimum par piscine
  const [tnEditing, setTnEditing] = useState(false);
  const [tnValue, setTnValue] = useState('');
  const [tnSaving, setTnSaving] = useState(false);
  const [empCompany, setEmpCompany] = useState('all');
  // Periode de la rentabilite : chantiers rattaches a leur date de DEBUT, travail filtre sur les jours pointes
  const [period, setPeriod] = useState({ preset: 'all', from: '', to: '' });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const userId = selectedUserId === 'me' ? undefined : selectedUserId;
      const params = userId ? `?user_id=${userId}` : '';
      
      const [attendanceRes, fuelRes, summaryRes] = await Promise.all([
        axios.get(`${API}/stats/attendance${params}`),
        axios.get(`${API}/stats/fuel${params}`),
        axios.get(`${API}/stats/summary${params}`)
      ]);
      setAttendanceStats(attendanceRes.data);
      setFuelStats(fuelRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const periodQuery = (p) => {
    const q = [];
    if (p?.from) q.push(`from=${p.from}`);
    if (p?.to) q.push(`to=${p.to}`);
    return q.length ? `?${q.join('&')}` : '';
  };

  const pad2 = (n) => String(n).padStart(2, '0');
  const applyPreset = (key) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const lastDay = (yy, mm) => new Date(yy, mm + 1, 0).getDate();
    if (key === 'all') return setPeriod({ preset: key, from: '', to: '' });
    if (key === 'this_year') return setPeriod({ preset: key, from: `${y}-01-01`, to: `${y}-12-31` });
    if (key === 'last_year') return setPeriod({ preset: key, from: `${y - 1}-01-01`, to: `${y - 1}-12-31` });
    if (key === 'this_month') return setPeriod({ preset: key, from: `${y}-${pad2(m + 1)}-01`, to: `${y}-${pad2(m + 1)}-${pad2(lastDay(y, m))}` });
    if (key === 'last_month') {
      const ly = m === 0 ? y - 1 : y;
      const lm = m === 0 ? 11 : m - 1;
      return setPeriod({ preset: key, from: `${ly}-${pad2(lm + 1)}-01`, to: `${ly}-${pad2(lm + 1)}-${pad2(lastDay(ly, lm))}` });
    }
    setPeriod({ ...period, preset: 'custom' });
  };

  const fetchProductivity = async (p = period) => {
    try {
      const res = await axios.get(`${API}/reports/productivity${periodQuery(p)}`);
      setProductivity(res.data);
    } catch (error) {
      console.error('Failed to fetch productivity:', error);
    }
  };

  const updFc = (idx, field, value) => setFcItems(fcItems.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));

  const saveFixedCharges = async () => {
    setFcSaving(true);
    try {
      const items = fcItems
        .filter((i) => (i.label || '').trim())
        .map((i) => ({ label: i.label.trim(), amount: parseFloat(i.amount) || 0 }));
      await axios.put(`${API}/settings/fixed-charges`, { items });
      toast.success(getText(language, 'Charges fixes enregistrées', 'Постоянные расходы сохранены', 'Cheltuieli fixe salvate', 'Постійні витрати збережено'));
      setFcEditing(false);
      fetchProductivity();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setFcSaving(false);
    }
  };

  const saveTargetNet = async () => {
    setTnSaving(true);
    try {
      await axios.put(`${API}/settings/target-net`, { pct: parseFloat(tnValue) || 0 });
      toast.success(getText(language, 'Objectif enregistré', 'Цель сохранена', 'Obiectiv salvat', 'Ціль збережено'));
      setTnEditing(false);
      fetchProductivity();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setTnSaving(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    if (isManager) fetchProductivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (users.length > 0) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, users]);

  useEffect(() => {
    if (isManager) fetchProductivity(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.from, period.to]);

  const COLORS = ['#0E2147', '#F59E0B', '#10B981', '#6366F1'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="stats-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
          <TrendingUp className="w-7 h-7" />
          {getText(language, 'Statistiques', 'Статистика', 'Statistici', 'Статистика')}
        </h1>
        
        {/* Employee Filter */}
        <div className="w-full sm:w-64">
          <Label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <User className="w-4 h-4" />
            {getText(language, 'Filtrer par employé', 'Фильтр по сотруднику', 'Filtrare după angajat', 'Фільтр за працівником')}
          </Label>
          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
            <SelectTrigger className="input-base">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="me">{getText(language, 'Moi', 'Я', 'Eu', 'Я')}</SelectItem>
              {users.map(user => (
                <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-base p-4">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-slate-500">
              {getText(language, 'Jours ce mois', 'Дней в этом месяце', 'Zile luna aceasta', 'Днів цього місяця')}
            </span>
          </div>
          <p className="text-3xl font-bold text-slate-900">{summary?.days_this_month || 0}</p>
        </div>
        
        <div className="card-base p-4">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-orange-600" />
            <span className="text-sm text-slate-500">
              {getText(language, 'Heures sup', 'Сверхурочные', 'Ore suplimentare', 'Понаднормові')}
            </span>
          </div>
          <p className="text-3xl font-bold text-slate-900">{summary?.overtime_hours || 0}h</p>
        </div>
        
        <div className="card-base p-4">
          <div className="flex items-center gap-3 mb-2">
            <Fuel className="w-5 h-5 text-green-600" />
            <span className="text-sm text-slate-500">
              {getText(language, 'Litres ce mois', 'Литров в этом месяце', 'Litri luna aceasta', 'Літрів цього місяця')}
            </span>
          </div>
          <p className="text-3xl font-bold text-slate-900">{summary?.liters_this_month?.toFixed(1) || 0}L</p>
        </div>
        
        <div className="card-base p-4">
          <div className="flex items-center gap-3 mb-2">
            <Fuel className="w-5 h-5 text-purple-600" />
            <span className="text-sm text-slate-500">
              {getText(language, 'Coût carburant', 'Стоимость топлива', 'Cost carburant', 'Вартість палива')}
            </span>
          </div>
          <p className="text-3xl font-bold text-slate-900">{summary?.fuel_cost_this_month?.toFixed(0) || 0}€</p>
        </div>
      </div>

      {/* Attendance Chart */}
      <div className="card-base p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          {getText(language, 'Jours travaillés par mois', 'Отработанные дни по месяцам', 'Zile lucrate pe lună', 'Відпрацьовані дні за місяць')}
        </h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={attendanceStats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 12 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="days" fill="#0E2147" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Fuel Chart */}
      <div className="card-base p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Fuel className="w-5 h-5" />
          {getText(language, 'Dépenses carburant par mois', 'Расходы на топливо по месяцам', 'Cheltuieli carburant pe lună', 'Витрати на паливо за місяць')}
        </h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={fuelStats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 12 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
                formatter={(value, name) => [
                  name === 'euros' ? `${value}€` : `${value}L`,
                  name === 'euros' 
                    ? getText(language, 'Coût', 'Стоимость', 'Cost', 'Вартість')
                    : getText(language, 'Litres', 'Литры', 'Litri', 'Літри')
                ]}
              />
              <Line type="monotone" dataKey="euros" stroke="#F59E0B" strokeWidth={3} dot={{ fill: '#F59E0B', r: 4 }} />
              <Line type="monotone" dataKey="liters" stroke="#10B981" strokeWidth={3} dot={{ fill: '#10B981', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#F59E0B]" />
            <span className="text-sm text-slate-600">{getText(language, 'Euros (€)', 'Евро (€)', 'Euro (€)', 'Євро (€)')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#10B981]" />
            <span className="text-sm text-slate-600">{getText(language, 'Litres (L)', 'Литры (L)', 'Litri (L)', 'Літри (L)')}</span>
          </div>
        </div>
      </div>

      {/* Overtime Distribution */}
      {summary?.overtime_distribution && summary.overtime_distribution.length > 0 && (
        <div className="card-base p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">
            {getText(language, 'Répartition des heures supplémentaires', 'Распределение сверхурочных', 'Distribuția orelor suplimentare', 'Розподіл понаднормових')}
          </h2>
          <div className="h-64 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={summary.overtime_distribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {summary.overtime_distribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Rentabilité par piscine (manager only) */}
      {isManager && productivity && (
        <div className="card-base p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
            <Euro className="w-5 h-5" />
            {getText(language, 'Rentabilité par piscine (main-d\'œuvre)', 'Рентабельность по бассейну (труд)', 'Rentabilitate pe piscină (manoperă)', 'Рентабельність по басейну (праця)')}
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            {getText(language,
              'Temps passé, coût main-d\'œuvre (jours pointés × taux journalier) et quote-part de charges fixes, regroupés par modèle de bassin. Coût complet = MO + charges fixes (la matière première viendra ensuite).',
              'Затраченное время и стоимость труда (отмеченные дни × дневная ставка), сгруппировано по модели бассейна.',
              'Timp petrecut și cost manoperă (zile pontate × tarif zilnic), grupat pe model de bazin.',
              'Витрачений час і вартість праці (відмічені дні × денна ставка), згруповано за моделлю басейну.')}
          </p>

          {/* Selecteur de periode */}
          <div className="mb-4 p-3 rounded-xl border border-slate-200 bg-white" data-testid="period-selector">
            <div className="flex flex-wrap gap-2 items-center">
              {[
                ['all', getText(language, 'Tout', 'Всё', 'Tot', 'Усе')],
                ['this_year', getText(language, 'Cette année', 'Этот год', 'Anul acesta', 'Цей рік')],
                ['last_year', getText(language, 'Année dernière', 'Прошлый год', 'Anul trecut', 'Минулий рік')],
                ['this_month', getText(language, 'Ce mois', 'Этот месяц', 'Luna aceasta', 'Цей місяць')],
                ['last_month', getText(language, 'Mois dernier', 'Прошлый месяц', 'Luna trecută', 'Минулий місяць')],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => applyPreset(key)}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${period.preset === key ? 'bg-[#0E2147] text-white border-[#0E2147]' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
                  data-testid={`period-${key}`}
                >
                  {label}
                </button>
              ))}
              <span className="text-xs text-slate-400 mx-1">{getText(language, 'ou du', 'или с', 'sau de la', 'або з')}</span>
              <input type="date" className="input-base text-sm py-1 w-auto" value={period.from} onChange={(e) => setPeriod({ preset: 'custom', from: e.target.value, to: period.to })} data-testid="period-from" />
              <span className="text-xs text-slate-400">{getText(language, 'au', 'по', 'până la', 'по')}</span>
              <input type="date" className="input-base text-sm py-1 w-auto" value={period.to} onChange={(e) => setPeriod({ preset: 'custom', from: period.from, to: e.target.value })} data-testid="period-to" />
            </div>
            <p className="text-xs text-slate-400 mt-2">
              {getText(language,
                'Les chantiers sont rattachés à la période de leur date de début (comptés en entier) ; la productivité et les jours perdus sont calculés sur les jours pointés dans la période.',
                'Объекты относятся к периоду по дате начала (целиком); продуктивность и потерянные дни считаются по отмеченным дням периода.',
                'Șantierele aparțin perioadei după data de început (în întregime); productivitatea și zilele pierdute se calculează pe zilele pontate din perioadă.',
                'Об\'єкти належать до періоду за датою початку (повністю); продуктивність і втрачені дні рахуються за відміченими днями періоду.')}
            </p>
          </div>

          {/* Volume : chantiers demarres */}
          {productivity.volume && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-center">
                <p className="text-2xl font-bold text-slate-900">{period.preset === 'all' ? productivity.volume.this_year : (productivity.volume.in_period ?? productivity.volume.this_year)}</p>
                <p className="text-xs text-slate-500">{period.preset === 'all' ? getText(language, 'Chantiers démarrés cette année', 'Объектов начато в этом году', 'Șantiere începute anul acesta', 'Об\'єктів почато цього року') : getText(language, 'Chantiers démarrés sur la période', 'Объектов начато за период', 'Șantiere începute în perioadă', 'Об\'єктів почато за період')}</p>
              </div>
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-center">
                <p className="text-2xl font-bold text-slate-900">{productivity.volume.this_month}</p>
                <p className="text-xs text-slate-500">{getText(language, 'Ce mois-ci', 'В этом месяце', 'Luna aceasta', 'Цього місяця')}</p>
              </div>
            </div>
          )}

          {productivity.data_quality?.pct_sans_chantier > 0 && (
            <div className="flex items-start gap-2 p-3 mb-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                {getText(language,
                  `Fiabilité : ${productivity.data_quality.pct_sans_chantier}% des pointages ne sont reliés à aucun chantier (${productivity.data_quality.sans_chantier}/${productivity.data_quality.total_pointages}). Ces jours ne sont pas comptés. Faites pointer chaque journée sur son chantier pour un coût réel.`,
                  `Надёжность: ${productivity.data_quality.pct_sans_chantier}% отметок без объекта (${productivity.data_quality.sans_chantier}/${productivity.data_quality.total_pointages}). Эти дни не учтены.`,
                  `Fiabilitate: ${productivity.data_quality.pct_sans_chantier}% pontaje fără șantier (${productivity.data_quality.sans_chantier}/${productivity.data_quality.total_pointages}). Aceste zile nu sunt numărate.`,
                  `Надійність: ${productivity.data_quality.pct_sans_chantier}% відміток без об'єкта (${productivity.data_quality.sans_chantier}/${productivity.data_quality.total_pointages}). Ці дні не враховані.`)}
              </span>
            </div>
          )}

          {/* Objectif de net minimum par piscine vendue (ordre Pavel 14/09/2026) */}
          <div className="mb-4 p-4 rounded-xl border border-indigo-200 bg-indigo-50" data-testid="target-net-card">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {getText(language, 'Objectif : marge nette minimum par piscine', 'Цель: минимальная чистая маржа на бассейн', 'Obiectiv: marjă netă minimă pe piscină', 'Ціль: мінімальна чиста маржа на басейн')}
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {Math.round(productivity.target_net_pct || 0)} %
                </p>
                <p className="text-xs text-slate-500">
                  {getText(language,
                    'Marge nette = net (prix de vente moins coût complet) rapporté au prix de vente. Une piscine dont la marge nette descend sous ce pourcentage passe en rouge.',
                    'Чистая маржа = чистая прибыль / цена продажи. Бассейн ниже этого процента отмечается красным.',
                    'Marjă netă = net / preț de vânzare. O piscină sub acest procent devine roșie.',
                    'Чиста маржа = чистий прибуток / ціна продажу. Басейн нижче цього відсотка стає червоним.')}
                </p>
              </div>
              <button type="button" onClick={() => { setTnValue(String(productivity.target_net_pct || '')); setTnEditing(!tnEditing); }} className="text-sm font-semibold underline" style={{ color: '#4338ca' }} data-testid="target-net-edit-btn">
                {tnEditing ? getText(language, 'Fermer', 'Закрыть', 'Închide', 'Закрити') : getText(language, 'Modifier', 'Изменить', 'Modifică', 'Змінити')}
              </button>
            </div>
            {tnEditing && (
              <div className="mt-3 flex items-center gap-2">
                <input type="number" min="0" max="100" step="0.5" className="input-base w-40" value={tnValue} onChange={(e) => setTnValue(e.target.value)} placeholder="ex. 10" data-testid="target-net-input" />
                <span className="text-sm text-slate-500">%</span>
                <button type="button" disabled={tnSaving} onClick={saveTargetNet} className="btn-primary px-4 py-2 text-sm rounded-lg" data-testid="target-net-save-btn">
                  {getText(language, 'Enregistrer', 'Сохранить', 'Salvează', 'Зберегти')}
                </button>
              </div>
            )}
          </div>

          {/* Charges fixes mensuelles : saisies ici, reparties au prorata des jours pointes */}
          <div className="mb-4 p-4 rounded-xl border border-slate-200 bg-slate-50" data-testid="fixed-charges-card">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  {getText(language, 'Charges fixes mensuelles', 'Ежемесячные постоянные расходы', 'Cheltuieli fixe lunare', 'Щомісячні постійні витрати')}
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {Math.round(productivity.fixed_charges?.monthly_total || 0).toLocaleString('fr-FR')} €
                  <span className="text-sm font-normal text-slate-500"> / {getText(language, 'mois', 'мес.', 'lună', 'міс.')}</span>
                </p>
                <p className="text-xs text-slate-500">
                  {getText(language,
                    `Soit ${Math.round(productivity.fixed_charges?.current_month_rate || 0)} € par jour travaillé ce mois-ci, réparti sur chaque chantier au prorata des jours pointés.`,
                    `Т.е. ${Math.round(productivity.fixed_charges?.current_month_rate || 0)} € за рабочий день в этом месяце, распределяется по объектам пропорционально отмеченным дням.`,
                    `Adică ${Math.round(productivity.fixed_charges?.current_month_rate || 0)} € pe zi lucrată luna aceasta, repartizat pe șantiere proporțional cu zilele pontate.`,
                    `Тобто ${Math.round(productivity.fixed_charges?.current_month_rate || 0)} € за робочий день цього місяця, розподіляється по об'єктах пропорційно відміченим дням.`)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setFcItems((productivity.fixed_charges?.items || []).map((i) => ({ ...i }))); setFcEditing(!fcEditing); }}
                className="text-sm font-semibold underline"
                style={{ color: 'var(--color-primary, #0E2147)' }}
                data-testid="fixed-charges-edit-btn"
              >
                {fcEditing
                  ? getText(language, 'Fermer', 'Закрыть', 'Închide', 'Закрити')
                  : getText(language, 'Modifier', 'Изменить', 'Modifică', 'Змінити')}
              </button>
            </div>

            {!fcEditing && productivity.fixed_charges?.items?.length > 0 && (
              <p className="mt-2 text-xs text-slate-500">
                {productivity.fixed_charges.items.map((i) => `${i.label} ${Math.round(i.amount)} €`).join(' · ')}
              </p>
            )}

            {fcEditing && (
              <div className="mt-3 space-y-2">
                {fcItems.map((it, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      className="input-base flex-1"
                      value={it.label}
                      onChange={(e) => updFc(idx, 'label', e.target.value)}
                      placeholder={getText(language, 'Libellé', 'Название', 'Denumire', 'Назва')}
                    />
                    <input
                      type="number"
                      className="input-base w-28"
                      value={it.amount}
                      onChange={(e) => updFc(idx, 'amount', e.target.value)}
                    />
                    <span className="text-sm text-slate-500">€</span>
                    <button type="button" onClick={() => setFcItems(fcItems.filter((_, i) => i !== idx))} className="text-red-500 p-1" aria-label="supprimer">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-3 pt-1">
                  <button type="button" onClick={() => setFcItems([...fcItems, { label: '', amount: 0 }])} className="text-sm underline text-slate-600">
                    + {getText(language, 'Ajouter une ligne', 'Добавить строку', 'Adaugă o linie', 'Додати рядок')}
                  </button>
                  <span className="flex-1" />
                  <span className="text-sm font-semibold text-slate-800">
                    {Math.round(fcItems.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0)).toLocaleString('fr-FR')} €
                  </span>
                  <button type="button" disabled={fcSaving} onClick={saveFixedCharges} className="btn-primary px-4 py-2 text-sm rounded-lg" data-testid="fixed-charges-save-btn">
                    {getText(language, 'Enregistrer', 'Сохранить', 'Salvează', 'Зберегти')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Table by model */}
          {productivity.by_model?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3">{getText(language, 'Modèle', 'Модель', 'Model', 'Модель')}</th>
                    <th className="py-2 px-3 text-right">{getText(language, 'Piscines', 'Бассейнов', 'Piscine', 'Басейнів')}</th>
                    <th className="py-2 px-3 text-right">{getText(language, 'Jours moy.', 'Дней сред.', 'Zile med.', 'Днів сер.')}</th>
                    <th className="py-2 px-3 text-right">{getText(language, 'MO moy.', 'Труд сред.', 'Man. med.', 'Праця сер.')}</th>
                    <th className="py-2 px-3 text-right">{getText(language, 'Matière moy.', 'Сырьё сред.', 'Materie med.', 'Сировина сер.')}</th>
                    <th className="py-2 px-3 text-right">{getText(language, 'Charges fixes moy.', 'Пост. расх. сред.', 'Chelt. fixe med.', 'Пост. витр. сер.')}</th>
                    <th className="py-2 px-3 text-right">{getText(language, 'Coût complet moy.', 'Полная стоим. сред.', 'Cost complet med.', 'Повна варт. сер.')}</th>
                    <th className="py-2 px-3 text-right">{getText(language, 'Prix vente moy.', 'Цена прод. сред.', 'Preț vânz. med.', 'Ціна прод. сер.')}</th>
                    <th className="py-2 pl-3 text-right">{getText(language, 'Net moy.', 'Чистая сред.', 'Net med.', 'Чистий сер.')}</th>
                  </tr>
                </thead>
                <tbody>
                  {productivity.by_model.map((m) => (
                    <tr key={m.pool_model} className="border-b border-slate-100">
                      <td className="py-2 pr-3 font-medium text-slate-900">{m.pool_model}</td>
                      <td className="py-2 px-3 text-right">{m.count}</td>
                      <td className="py-2 px-3 text-right">{m.avg_days} j</td>
                      <td className="py-2 px-3 text-right">{Math.round(m.avg_labor_cost)} €</td>
                      <td className="py-2 px-3 text-right">{Math.round(m.avg_material_cost || 0)} €</td>
                      <td className="py-2 px-3 text-right">{Math.round(m.avg_fixed_cost || 0)} €</td>
                      <td className="py-2 px-3 text-right font-semibold">{Math.round(m.avg_total_cost || m.avg_labor_cost)} €</td>
                      <td className="py-2 px-3 text-right">{m.avg_sale_price != null ? `${Math.round(m.avg_sale_price)} €` : '-'}</td>
                      <td className="py-2 pl-3 text-right font-bold">
                        {m.avg_net != null ? (
                          <span className={(m.avg_net_pct || 0) >= (productivity.target_net_pct || 0) ? 'text-emerald-600' : 'text-red-600'}>
                            {Math.round(m.avg_net).toLocaleString('fr-FR')} € ({m.avg_net_pct != null ? m.avg_net_pct : 0} %)
                          </span>
                        ) : <span className="text-slate-400">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-sm text-slate-500 p-3 rounded-lg bg-slate-50 border border-slate-200">
              {getText(language,
                'Aucune piscine encore renseignée. Ouvrez un chantier dans « Chantiers » et remplissez le champ « Modèle / taille du bassin » pour qu\'il entre dans ce calcul.',
                'Пока нет бассейнов. Заполните поле «Модель / размер бассейна» в объекте.',
                'Nicio piscină încă. Completați câmpul «Model / dimensiune bazin» într-un șantier.',
                'Ще немає басейнів. Заповніть поле «Модель / розмір басейну» в об\'єкті.')}
            </div>
          )}

          {/* Prévu vs réel par chantier */}
          {(productivity.pools?.length > 0 || productivity.untagged?.length > 0) && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">
                {getText(language, 'Prévu vs réel par chantier', 'План vs факт по объекту', 'Prevăzut vs real pe șantier', 'План vs факт по об\'єкту')}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-2 pr-3">{getText(language, 'Chantier', 'Объект', 'Șantier', 'Об\'єкт')}</th>
                      <th className="py-2 px-3 text-right">{getText(language, 'Doit dépenser (estim.)', 'Должны потратить (оценка)', 'Trebuie cheltuit (estim.)', 'Маємо витратити (оцінка)')}</th>
                      <th className="py-2 px-3 text-right">{getText(language, 'A dépensé', 'Потрачено', 'Cheltuit', 'Витрачено')}</th>
                      <th className="py-2 px-3 text-right">{getText(language, 'Écart', 'Разница', 'Diferență', 'Різниця')}</th>
                      <th className="py-2 px-3 text-right">{getText(language, 'Doit gagner', 'Должны заработать', 'Trebuie câștigat', 'Маємо заробити')}</th>
                      <th className="py-2 pl-3 text-right">{getText(language, 'A gagné', 'Заработано', 'Câștigat', 'Зароблено')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...(productivity.pools || []), ...(productivity.untagged || [])].map((p) => (
                      <tr key={p.project_id} className="border-b border-slate-100">
                        <td className="py-2 pr-3 text-slate-800">{p.name}</td>
                        <td className="py-2 px-3 text-right text-slate-500">{p.budget_cost != null ? `${Math.round(p.budget_cost)} €${p.budget_source === 'manuel' ? '*' : ''}` : <span className="text-slate-400">-</span>}</td>
                        <td className="py-2 px-3 text-right">{Math.round(p.full_cost)} €</td>
                        <td className="py-2 px-3 text-right">
                          {p.variance_cost != null ? (
                            <span className={p.variance_cost > 0 ? 'text-red-600' : 'text-emerald-600'}>
                              {p.variance_cost > 0 ? '+' : ''}{Math.round(p.variance_cost)} €
                            </span>
                          ) : <span className="text-slate-400">-</span>}
                        </td>
                        <td className="py-2 px-3 text-right text-slate-500">{p.target_net != null ? `${Math.round(p.target_net)} €` : '-'}</td>
                        <td className="py-2 pl-3 text-right font-semibold">
                          {p.net != null ? (
                            <span className={p.meets_target ? 'text-emerald-600' : 'text-red-600'}>{Math.round(p.net)} €</span>
                          ) : <span className="text-slate-400">-</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {getText(language, '* budget saisi à la main. Sinon estimé (matière + main-d\'œuvre estimée + charges fixes) dès que le chantier a un modèle ou des coûts matière ; « - » = pas encore estimable. « A gagné » et « doit gagner » viennent du prix de vente (devis signé) saisi sur le chantier.',
                  '* бюджет вручную. Иначе оценка. «Заработано» появляется при заданной цене продажи.',
                  '* buget manual. Altfel estimat. „Câștigat” apare cu preț de vânzare.',
                  '* бюджет вручну. Інакше оцінка. «Зароблено» з\'являється з ціною продажу.')}
              </p>
            </div>
          )}

          {/* Productivité par salarié */}
          {productivity.employees?.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <h3 className="text-sm font-semibold text-slate-700">
                  {getText(language, 'Productivité par salarié', 'Производительность по сотруднику', 'Productivitate pe angajat', 'Продуктивність по працівнику')}
                </h3>
                <select className="input-base text-sm w-auto py-1" value={empCompany} onChange={(e) => setEmpCompany(e.target.value)} data-testid="emp-company-filter">
                  <option value="all">{getText(language, 'Toutes sociétés', 'Все компании', 'Toate companiile', 'Усі компанії')}</option>
                  <option value="La Piscine Container">La Piscine Container</option>
                  <option value="Eco Bati Construction">Eco Bati Construction</option>
                </select>
              </div>
              {productivity.lost_total?.days > 0 && (
                <div className="flex items-start gap-2 p-3 mb-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    {getText(language,
                      `Jours perdus (salariés La Piscine Container au dépôt / interne) : ${productivity.lost_total.days} jours, soit ${Math.round(productivity.lost_total.cost).toLocaleString('fr-FR')} € de main-d'œuvre non rattachée à un client.`,
                      `Потерянные дни (сотрудники LPC на складе): ${productivity.lost_total.days} дн., ${Math.round(productivity.lost_total.cost)} €.`,
                      `Zile pierdute (angajați LPC la depozit): ${productivity.lost_total.days} zile, ${Math.round(productivity.lost_total.cost)} €.`,
                      `Втрачені дні (працівники LPC на складі): ${productivity.lost_total.days} дн., ${Math.round(productivity.lost_total.cost)} €.`)}
                  </span>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-2 pr-3">{getText(language, 'Salarié', 'Сотрудник', 'Angajat', 'Працівник')}</th>
                      <th className="py-2 px-3 text-right">{getText(language, 'Jours', 'Дни', 'Zile', 'Дні')}</th>
                      <th className="py-2 px-3 text-right">{getText(language, 'Perdus', 'Потеряно', 'Pierdute', 'Втрачені')}</th>
                      <th className="py-2 px-3 text-right">{getText(language, 'Productivité', 'Продуктивность', 'Productivitate', 'Продуктивність')}</th>
                      <th className="py-2 px-3 text-right">{getText(language, 'Coût perdu', 'Потери €', 'Cost pierdut', 'Втрати €')}</th>
                      <th className="py-2 pl-3 text-right">{getText(language, 'Valeur produite', 'Произведено €', 'Valoare produsă', 'Створено €')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productivity.employees.filter((e) => empCompany === 'all' || e.company === empCompany).map((e) => (
                      <tr key={e.user_id} className="border-b border-slate-100">
                        <td className="py-2 pr-3">
                          <span className="text-slate-800 font-medium">{e.name}</span>
                          <span className="block text-xs text-slate-400">{e.company}</span>
                        </td>
                        <td className="py-2 px-3 text-right">{e.total_days}</td>
                        <td className="py-2 px-3 text-right">{e.lost_days > 0 ? <span className="text-red-600">{e.lost_days}</span> : '0'}</td>
                        <td className="py-2 px-3 text-right font-semibold">
                          <span className={(e.productivity_pct || 0) >= 90 ? 'text-emerald-600' : (e.productivity_pct || 0) >= 70 ? 'text-amber-600' : 'text-red-600'}>
                            {e.productivity_pct != null ? `${e.productivity_pct} %` : '-'}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right">{e.lost_cost > 0 ? `${Math.round(e.lost_cost)} €` : '-'}</td>
                        <td className="py-2 pl-3 text-right text-slate-600">{e.value_produced > 0 ? `${Math.round(e.value_produced).toLocaleString('fr-FR')} €` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Untagged projects with hours */}
          {productivity.untagged?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">
                {getText(language, 'Chantiers avec des heures mais sans modèle (à taguer)', 'Объекты с часами без модели', 'Șantiere cu ore fără model', 'Об\'єкти з годинами без моделі')}
              </h3>
              <div className="space-y-1">
                {productivity.untagged.map((p) => (
                  <div key={p.project_id} className="flex items-center justify-between text-sm py-1.5 px-3 rounded bg-slate-50">
                    <span className="text-slate-700 truncate mr-2">{p.name}</span>
                    <span className="text-slate-500 whitespace-nowrap">{p.days} j · MO {Math.round(p.labor_cost)} € · CF {Math.round(p.fixed_cost || 0)} €{p.net != null ? ` · Net ` : ''}{p.net != null ? <span className={p.meets_target ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>{Math.round(p.net)} €{p.net_pct != null ? ` (${p.net_pct} %)` : ''}</span> : ''}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {getText(language,
                  'Renseignez leur modèle dans « Chantiers » pour les faire entrer dans le calcul par taille. Un chantier qui n\'est pas une piscine se coche « Projet interne ».',
                  'Укажите их модель в «Объектах».',
                  'Completați modelul lor în «Șantiere».',
                  'Вкажіть їх модель у «Об\'єктах».')}
              </p>
            </div>
          )}

          {/* Projets internes (dépôt, Fitou...) : à part, hors rentabilité piscine */}
          {productivity.internal?.length > 0 && (
            <div className="mt-6 pt-4 border-t border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">
                {getText(language, 'Projets internes (hors rentabilité piscine)', 'Внутренние проекты (вне рентабельности бассейнов)', 'Proiecte interne (în afara rentabilității piscinelor)', 'Внутрішні проєкти (поза рентабельністю басейнів)')}
              </h3>
              <div className="space-y-1">
                {productivity.internal.map((p) => (
                  <div key={p.project_id} className="flex items-center justify-between text-sm py-1.5 px-3 rounded bg-slate-100">
                    <span className="text-slate-700 truncate mr-2">{p.name}</span>
                    <span className="text-slate-500 whitespace-nowrap">{p.days} j · {Math.round(p.labor_cost)} €</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-sm py-1.5 px-3 font-semibold text-slate-800">
                  <span>{getText(language, 'Total interne', 'Итого внутренние', 'Total intern', 'Разом внутрішні')}</span>
                  <span className="whitespace-nowrap">{productivity.internal_totals?.days} j · {Math.round(productivity.internal_totals?.labor_cost || 0)} €</span>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {getText(language,
                  'Dépôt, atelier, maison Fitou : ce temps est réel mais n\'est imputé à aucune piscine. Il n\'entre pas dans le coût par taille.',
                  'Склад, цех, дом Fitou: это время реально, но не относится ни к одному бассейну.',
                  'Depozit, atelier, casa Fitou: timp real, dar neatribuit niciunei piscine.',
                  'Склад, цех, будинок Fitou: час реальний, але не віднесений до жодного басейну.')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
